import {
    getExternalJobUrl,
    GetExternalJobUrlGeneratorType,
    isSignedResourceAvailable,
    Nullable,
    safeSaga,
} from "@data-force/app";
import { ContentHeadResponse } from "@data-force/app/interfaces/store/sagas/utils";
import {
    all,
    call,
    put,
    PutEffect,
    select,
    SelectEffect,
    TakeEffect,
    takeEvery,
    takeLatest,
} from "@redux-saga/core/effects";
import axios, { AxiosResponse } from "axios";
import { SyntheticEvent } from "react";
import { CallEffect } from "redux-saga/effects";
import {
    ActivityNotification,
    ContentType,
    FilterType,
    JobTag,
    MessageAction,
    PhaseStatus,
    ProjectType,
} from "../../enums/enums";
import {
    ClaimJobPhaseAction,
    CompleteJobPhaseAction,
    DestroyDetailsDrawerAction,
    GetJobListAction,
    GetJobPhaseWorkflowDefinitionsAction,
    OpenCtWebJobAction,
    OpenExternalJobAction,
    OpenJobEditorAction,
    SetActiveJobTypeAction,
    SetChromeExtensionMissingVisibleAction,
    SetExternalJobUrlAction,
    SetJobListAction,
    SetJobListCursorAction,
    SetJobPhaseWorkflowDefinitionsAction,
    SetPhaseConfigListAction,
    SetSelectedJobAction,
    ShowActivityNotificationAction,
    ShowDetailsDrawerAction,
    SignResourceAction,
    UpdateJobListAction,
} from "../../interfaces/store/actions/job";
import { DisplayErrorMessageAction } from "../../interfaces/store/actions/main";
import { SetExternalUrlLoadingAction, SetLoadingAction } from "../../interfaces/store/actions/mask";
import {
    JobEntity,
    MessagePayload,
    PhaseConfig,
    PhaseConfigNormalized,
} from "../../interfaces/store/actions/payload/job";
import { Task, TaskPhaseStats } from "../../interfaces/store/actions/payload/task";
import { WorkflowDefinition } from "../../interfaces/store/actions/payload/workflowDefinition";
import {
    ClaimJobPhaseResponse,
    ContentGetResponse,
    GetExternalJobUrlResponse,
    GetJobListPhaseStatsResponse,
    GetJobPhaseListResponse,
    GetPhaseDirectionsResponse,
} from "../../interfaces/store/sagas/job";
import { GetTaskListResponse, TaskInfo } from "../../interfaces/store/sagas/task";
import StoreState from "../../interfaces/store/storeState";
import { SetActiveTestTypeAction } from "../../interfaces/store/test";
import { isCtWebJob, isExternalProject } from "../../utils/common";
import { DEFAULT_ORIGIN, DrawerTabs } from "../../utils/constants";
import { TabManager } from "../../utils/TabManager";
import {
    CLAIM_JOB_PHASE,
    COMPLETE_JOB_PHASE,
    GET_JOB_LIST,
    GET_JOB_PHASE_WORKFLOW_DEFINITIONS,
    GET_PHASE_CONFIG_LIST,
    GET_PHASE_DIRECTIONS,
    OPEN_CT_WEB_JOB,
    OPEN_EXTERNAL_JOB,
    SIGN_RESOURCE,
} from "../actions/availableActions";
import {
    destroyDetailsDrawer,
    getJobList,
    getJobPhaseWorkflowDefinitions,
    openCtWebJob,
    openExternalJob,
    openJobEditor,
    setActiveJobType,
    setChromeExtensionMissingVisible,
    setExternalJobUrl,
    setJobList,
    setJobListCursor,
    setJobPhaseWorkflowDefinitions,
    setPhaseConfigList,
    setSelectedJob,
    showActivityNotification,
    showDetailsDrawer,
    updateJobList,
} from "../actions/job";
import { setExternalUrlLoading, setLoading } from "../actions/mask";
import { getTaskInfo } from "../actions/task";
import { setActiveTestType } from "../actions/test";
import { selectPhaseConfigList } from "../selectors/job";
import {
    getActiveJobFilter, getAnnotationUrl, getBaseUrl, getEditorGatewayUrl, getJobTestType, getTranscriptionUrl,
} from "./main";
import { fetchAllTasks, getTaskInfoHandler } from "./task";

type CompleteJobPhaseHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse>
    | PutEffect<GetJobListAction | ShowActivityNotificationAction>;

function* completeJobPhaseHandler(
    {
        payload: {
            projectId, jobId, event, phase, title, notificationType,
        },
    }: CompleteJobPhaseAction,
): IterableIterator<CompleteJobPhaseHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();

    const axiosResponse: Nullable<AxiosResponse> = yield axios.post(`${baseUrl}/project/${projectId}/job/${jobId}/complete`, {
        event,
        phase,
    });

    if (axiosResponse) {
        const {status} = axiosResponse;
        yield put(showActivityNotification({
            phaseName: phase,
            title,
            type: status === 200 ? notificationType as string : ActivityNotification.ERROR,
        }));
    }

    const searchValue: Nullable<string> = yield select((state: StoreState): Nullable<string> => state.job.searchValue);
    const filter: Nullable<string> = yield getActiveJobFilter();
    const isTest: Nullable<boolean> = yield getJobTestType();

    yield put(getJobList({
        title: searchValue,
        filter,
        isTest,
    }));
}

type GetJobPhaseWDType =
    SelectEffect
    | Promise<AxiosResponse<WorkflowDefinition[]>>
    | PutEffect<SetJobPhaseWorkflowDefinitionsAction | SetJobListAction>;

function* getJobPhaseWDHandler(): IterableIterator<GetJobPhaseWDType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const axiosResponse: Nullable<AxiosResponse<WorkflowDefinition[]>> = yield axios.get(`${baseUrl}/workflow-definition`);

    if (axiosResponse) {
        const {data}: AxiosResponse<WorkflowDefinition[]> = axiosResponse;
        yield put(setJobPhaseWorkflowDefinitions(data));
    }
}

type GetJobListHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<GetJobListPhaseStatsResponse | GetJobPhaseListResponse>>
    | PutEffect<GetJobPhaseWorkflowDefinitionsAction
    | SetLoadingAction
    | SetJobListAction
    | SetJobListCursorAction
    | UpdateJobListAction>;

function* getJobListHandler({payload}: GetJobListAction): IterableIterator<GetJobListHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    yield put(setLoading(true));
    const {EXPIRED}: typeof JobTag = JobTag;
    const statuses: string[] = [...(payload?.decoratedStatuses || [])];
    let testStatuses: string[] = [...(payload?.statuses || [])];
    const sortDirection: Nullable<string> = payload?.sortDirection || (yield select(
        (state: StoreState): Nullable<string> => state.job.sortDirection,
    ));

    // As per BE request - when test statuses contain expired status we remove that status from test statuses
    // and send it separately within plain statuses
    if (testStatuses.includes(EXPIRED)) {
        testStatuses = testStatuses.filter((status: string): boolean => status !== EXPIRED);
        if (!statuses.includes(EXPIRED)) {
            statuses.push(EXPIRED);
        }
    }

    const jobPhaseResponse: Nullable<AxiosResponse<GetJobPhaseListResponse>> = yield axios.get(`${baseUrl}/job/phase`, {
        params: {
            ...payload?.cursor,
            sortDirection,
            title: payload?.title,
            filter: payload?.filter,
            test: payload?.isTest,
            testSkillFilter: payload?.skills,
            testTypeFilter: payload?.types,
            scoreStatusFilter: testStatuses,
            decoratedStatuses: statuses,
        },
    });

    if (jobPhaseResponse) {
        const {data}: AxiosResponse<GetJobPhaseListResponse> = jobPhaseResponse;
        let jobList: JobEntity[] = data?.items || [];
        jobList = jobList.map((job: JobEntity): JobEntity => {
            const {phaseStats}: JobEntity = job;
            let completedTaskCount: number = 0;

            if (phaseStats) {
                const {
                    COMPLETED = 0,
                    REJECTED = 0,
                }: TaskPhaseStats = phaseStats;
                completedTaskCount = COMPLETED + REJECTED;
            }
            return {
                ...job,
                completedTaskCount,
            };
        });

        const workflowDefinitionsLength: Nullable<number> = yield select(
            (state: StoreState): Nullable<number> => state.job.workflowDefinitions.length,
        );

        if (!workflowDefinitionsLength) {
            yield put(getJobPhaseWorkflowDefinitions());
        }

        if (payload?.updateList) {
            yield put(updateJobList(jobList));
        } else {
            yield put(setJobList(jobList));
        }

        if (data.cursor) {
            yield put(setJobListCursor(data.cursor));
        }
    }

    const projectType: Nullable<string> = yield select(
        (state: StoreState): Nullable<string> => state.job.selectedJob?.projectType,
    );
    const isExternal: boolean = !!payload?.openEditor && (projectType === ProjectType.EXTERNAL);

    yield put(setLoading(isExternal));
}

type ClaimJobPhaseIteratorType =
    SelectEffect
    | TakeEffect
    | Promise<AxiosResponse>
    | PutEffect<GetJobListAction
    | SetActiveJobTypeAction
    | SetActiveTestTypeAction
    | DisplayErrorMessageAction
    | OpenJobEditorAction
    | SetSelectedJobAction
    | ShowActivityNotificationAction
    | ShowDetailsDrawerAction
    | OpenExternalJobAction
    | OpenCtWebJobAction>;

function* claimJobPhaseHandler({
    payload: {
        projectId, jobId, phaseName, openEditor, open, title, test,
    },
}: ClaimJobPhaseAction): IterableIterator<ClaimJobPhaseIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const isTest: Nullable<boolean> = yield getJobTestType();
    const {AVAILABLE, IN_PROGRESS}: typeof ContentType = ContentType;

    try {
        const claimJobPhaseResponse: Nullable<AxiosResponse<ClaimJobPhaseResponse>> = yield axios.post(
            `${baseUrl}/project/${projectId}/job/${jobId}/claim`,
            {phaseName},
        );
        if (claimJobPhaseResponse) {
            const {data, status}: AxiosResponse<ClaimJobPhaseResponse> = claimJobPhaseResponse;
            yield put(isTest ? setActiveTestType(IN_PROGRESS) : setActiveJobType(IN_PROGRESS));
            yield put(getJobList({
                filter: FilterType.ONGOING,
                isTest,
                openEditor,
            }));

            if (open && !test) {
                yield put(showActivityNotification({
                    phaseName,
                    title,
                    type: status === 200 ? ActivityNotification.COMPLETE : ActivityNotification.ERROR,
                    jobId: data.codename,
                }));
            }

            const axiosResponse: Nullable<AxiosResponse<JobEntity>> = yield axios.get(
                `${baseUrl}/project/${data.projectId}/job/${data.jobId}/phase/${data.jobPhaseId}`,
            );
            if (axiosResponse) {
                const {data: jobData}: AxiosResponse<JobEntity> = axiosResponse;
                yield put(setSelectedJob(jobData));
                const {id: jobPhaseId, projectType, taskCount}: JobEntity = jobData;
                const phaseConfigList: PhaseConfigNormalized | undefined = (
                    yield select(selectPhaseConfigList)
                ) as PhaseConfigNormalized | undefined;

                if (isExternalProject(projectType) && openEditor) {
                    yield put(showDetailsDrawer(DrawerTabs.STATS));
                    if (taskCount === 1) {
                        yield put(openExternalJob({projectId, jobId, phaseName}));
                    }
                } else if (openEditor && phaseConfigList && isCtWebJob(phaseConfigList, phaseName, projectType)) {
                    yield put(openCtWebJob({
                        projectId, jobId: data.jobId, phase: phaseName, jobPhaseId,
                    }));
                } else {
                    yield put(openJobEditor(openEditor));
                }
            }
        }
    } catch (e: any) {
        yield put(isTest ? setActiveTestType(AVAILABLE) : setActiveJobType(AVAILABLE));
        yield put(getJobList({
            filter: FilterType.CLAIMABLE,
            isTest,
        }));

        if (TabManager.getInstance()) {
            TabManager.getInstance().closeTab();
        }
    }
}

type GetPhaseConfigListHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<PhaseConfig[]>>
    | PutEffect<SetPhaseConfigListAction>;

function* getPhaseConfigListHandler(): IterableIterator<GetPhaseConfigListHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const axiosResponse: Nullable<AxiosResponse<PhaseConfig[]>> = yield axios.get(`${baseUrl}/phase-config`);
    if (axiosResponse) {
        const {data}: AxiosResponse<PhaseConfig[]> = axiosResponse;
        const normalizedData: PhaseConfigNormalized = {};
        data.forEach((phaseConfig: PhaseConfig): void => {
            normalizedData[`${phaseConfig.projectType}-${phaseConfig.name}`] = phaseConfig;
        });
        yield put(setPhaseConfigList(normalizedData));
    }
}

type SignResourcesHandlerIteratorType =
    SelectEffect
    | CallEffect
    | Promise<AxiosResponse<ContentHeadResponse>>;

function* signResourceHandler(element: SignResourceAction): IterableIterator<SignResourcesHandlerIteratorType> {
    const syntheticEvent: SyntheticEvent = element.payload;

    syntheticEvent.persist(); // has to be persisted since syntheticEvent is reused for optimization (https://legacy.reactjs.org/docs/legacy-event-pooling.html)

    const {target}: any = syntheticEvent; // target's type is any as in interface BaseSyntheticEvent

    const projectType: string = target.getAttribute("projecttype");
    let baseUrl : Nullable<string>;
    switch (projectType) {
        case ProjectType.ANNOTATION:
            baseUrl = yield getAnnotationUrl();
            break;
        case ProjectType.TRANSCRIPTION:
            baseUrl = yield getTranscriptionUrl();
            break;
        default:
            baseUrl = "";
            break;
    }

    if (baseUrl) {
        const beginningOfUrl: string = `${document.location.origin}${document.location.pathname}`;
        const fileReference: string = target.src.replace(beginningOfUrl, "");
        // @ts-ignore
        const resourceAvailable: Nullable<boolean> = yield call(isSignedResourceAvailable, {
            baseUrl,
            fileReference,
        });

        if (resourceAvailable) {
            const urlAxiosResponse: Nullable<AxiosResponse<ContentGetResponse>> = yield axios.get(`${baseUrl}/sign`, {
                params: {
                    key: fileReference,
                    method: "GET",
                },
            });

            if (urlAxiosResponse) {
                const {data: {contentUrl}}: AxiosResponse<ContentGetResponse> = urlAxiosResponse;

                target.src = contentUrl;
            }
        }
    }
}

type OpenExternalJobHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<GetExternalJobUrlResponse | GetTaskListResponse>>
    | PutEffect<SetExternalJobUrlAction | OpenJobEditorAction | SetExternalUrlLoadingAction | DestroyDetailsDrawerAction>
    | GetExternalJobUrlGeneratorType;

function* openExternalJobHandler({
    payload: {
        projectId, jobId, phaseName, taskId,
    },
}: OpenExternalJobAction): IterableIterator<OpenExternalJobHandlerIteratorType> {
    yield put(setExternalUrlLoading(true));
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const gatewayUrl: Nullable<string> = yield getEditorGatewayUrl();
    let payloadTaskId: Nullable<string> = taskId;
    try {
        if (!payloadTaskId) {
            const tasksAxiosResponse: Nullable<AxiosResponse<GetTaskListResponse>> = yield axios.get(
                `${baseUrl}/project/${projectId}/job/${jobId}/task`,
            );
            if (tasksAxiosResponse) {
                const {data: {items}}: AxiosResponse<GetTaskListResponse> = tasksAxiosResponse;

                if (items.length) {
                    payloadTaskId = items[0].id;
                }
            }
        }

        if (payloadTaskId && gatewayUrl) {
            const url: Nullable<string> = yield getExternalJobUrl({
                projectId, jobId, taskId: payloadTaskId, phase: phaseName, gatewayUrl,
            });

            if (url) {
                yield put(setExternalJobUrl(url));
                yield put(openJobEditor(true));
            }
        }
    } catch {
        if (taskId) {
            yield put(destroyDetailsDrawer());
        }
    } finally {
        yield put(setExternalUrlLoading(false));
    }
}

type GetPhaseDirectionsHandlerIteratorType =
    SelectEffect
    | Promise<AxiosResponse<GetPhaseDirectionsResponse>>
    | PutEffect<SetLoadingAction | SetSelectedJobAction>;

function* getPhaseDirectionsHandler(): IterableIterator<GetPhaseDirectionsHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getBaseUrl();
    let selectedJob: Nullable<JobEntity> = yield select((state: StoreState): Nullable<JobEntity> => state.job.selectedJob);

    if (selectedJob) {
        const {id, projectId, longDescriptionRefId}: JobEntity = selectedJob;

        const axiosResponse: Nullable<AxiosResponse<GetPhaseDirectionsResponse>> = yield axios.get(
            `${baseUrl}/project/${projectId}/phase/${id}/reference/${longDescriptionRefId}`,
        );

        if (axiosResponse) {
            const {data: {refValue}}: AxiosResponse<GetPhaseDirectionsResponse> = axiosResponse;
            selectedJob = {...selectedJob as JobEntity, longDescription: refValue};
            yield put(setSelectedJob(selectedJob));
        }
    }
}

function requestDomains(): Promise<string[]> {
    return new Promise((resolve): void => {
        const handleMessage = (event: MessageEvent<MessagePayload>) => {
            const {action, data}: MessagePayload = event.data;
            const messageAction: MessageAction = MessageAction[action as keyof typeof MessageAction];
            if (messageAction === MessageAction.DOMAINS && data) {
                resolve(data);
                window.removeEventListener("message", handleMessage);
            }
        };

        window.addEventListener("message", handleMessage);
        window.postMessage({action: MessageAction.REQUEST_DOMAINS}, DEFAULT_ORIGIN);

        // Resolve as empty, if domains are not provided within a second
        setTimeout(() => {
            window.removeEventListener("message", handleMessage);
            resolve([]);
        }, 1000);
    });
}

type OpenCtWebJobHandlerIteratorType = SelectEffect
| CallEffect
| Promise<string[]>
| PutEffect<SetChromeExtensionMissingVisibleAction>;

function* openCtWebJobHandler({
    payload: {
        projectId, jobId, phase, taskId, jobPhaseId,
    },
}: OpenCtWebJobAction): IterableIterator<OpenCtWebJobHandlerIteratorType> {
    const tabManager: TabManager = TabManager.getInstance();
    if (tabManager.hasOpenedTab()) {
        tabManager.closeTab();
    }

    const ctWebDomains: string[] | undefined = (yield requestDomains()) as string[] | undefined;
    if (!ctWebDomains?.length) {
        yield put(setChromeExtensionMissingVisible(true));
        return;
    }

    let taskEntityId: string | undefined = taskId;
    if (!taskEntityId) {
        const tasks: Task[] | undefined = (yield call(fetchAllTasks)) as Task[] | undefined;
        if (!tasks?.length) {
            throw Error("CT Web job cannot be opened - no tasks");
        }
        const readyTask: Task | undefined = tasks.find(
            (task: Task): boolean => [
                PhaseStatus.READY,
                PhaseStatus.IN_PROGRESS,
                PhaseStatus.REOPENED,
            ].includes(task.status),
        );
        taskEntityId = readyTask?.id || tasks[0]?.id;
    }
    if (!taskEntityId) {
        throw Error("CT Web job cannot be opened - no task");
    }

    const taskInfo: Nullable<TaskInfo> = (yield call(getTaskInfoHandler, getTaskInfo({
        projectId,
        taskId: taskEntityId,
        jobId,
        phase,
    }))) as Nullable<TaskInfo>;
    if (!taskInfo) {
        throw Error("CT Web job cannot be opened - no task info");
    }

    window.postMessage({
        action: MessageAction.TASK_PARAMS,
        data: {
            ...taskInfo,
            projectId,
            jobId,
            phase,
            taskEntityId,
            jobPhaseId,
        },
    }, DEFAULT_ORIGIN);
}

export function* jobSagaActionWatcher() {
    yield all([
        takeEvery(SIGN_RESOURCE, safeSaga(signResourceHandler)),
        takeLatest(COMPLETE_JOB_PHASE, safeSaga(completeJobPhaseHandler)),
        takeLatest(GET_JOB_PHASE_WORKFLOW_DEFINITIONS, safeSaga(getJobPhaseWDHandler)),
        takeLatest(GET_JOB_LIST, safeSaga(getJobListHandler)),
        takeLatest(CLAIM_JOB_PHASE, safeSaga(claimJobPhaseHandler)),
        takeLatest(GET_PHASE_CONFIG_LIST, safeSaga(getPhaseConfigListHandler)),
        takeLatest(OPEN_EXTERNAL_JOB, safeSaga(openExternalJobHandler)),
        takeLatest(GET_PHASE_DIRECTIONS, safeSaga(getPhaseDirectionsHandler)),
        takeLatest(OPEN_CT_WEB_JOB, safeSaga(openCtWebJobHandler)),
    ]);
}
